
import { jitsiLocalStorage } from '@jitsi/js-utils';
import _ from 'lodash';

import { getFeatureFlag } from '../flags/functions';
import { parseURLParams } from '../util';

import CONFIG_WHITELIST from './configWhitelist';
import { FEATURE_FLAGS, _CONFIG_STORE_PREFIX } from './constants';
import INTERFACE_CONFIG_WHITELIST from './interfaceConfigWhitelist';

// import logger from './logger';

/**
 * MergeWith customizer that overwrites arrays instead of merging them.
 *
 * @param {*} oldValue - The old value.
 * @param {*} newValue - The new value.
 * @returns {*} The new value if the old is an array, undefined otherwise.
 */
function _mergeWithArrayOverwrite(oldValue, newValue) {
    if (Array.isArray(oldValue)) {
        return newValue;
    }
}

// XXX The function getRoomName is split out of
// functions.js because it is bundled in both app.bundle and
// do_external_connect, webpack 1 does not support tree shaking, and we don't
// want all functions to be bundled in do_external_connect.
export { default as getRoomName } from './getRoomName';

/**
 * Create a "fake" configuration object for the given base URL. This is used in case the config
 * couldn't be loaded in the welcome page, so at least we have something to try with.
 *
 * @param {string} baseURL - URL of the deployment for which we want the fake config.
 * @returns {Object}
 */
export function createFakeConfig(baseURL: string) {
    const url = new URL(baseURL);

    return {
        hosts: {
            domain: url.hostname,
            muc: `conference.${url.hostname}`
        },
        bosh: `${baseURL}http-bind`,
        clientNode: 'https://meethour.io/meet-hour',
        p2p: {
            enabled: true
        }
    };
}


/**
 * Overrides JSON properties in {@code config} and
 * {@code interfaceConfig} Objects with the values from {@code newConfig}.
 * Overrides only the whitelisted keys.
 *
 * @param {Object} config - The config Object in which we'll be overriding
 * properties.
 * @param {Object} interfaceConfig - The interfaceConfig Object in which we'll
 * be overriding properties.
 * @param {Object} loggingConfig - The loggingConfig Object in which we'll be
 * overriding properties.
 * @param {Object} json - Object containing configuration properties.
 *  @param {Function} dispatch - Dispatch a Function.
 *
 * Destination object is selected based on root property name:
 * {
 *     config: {
 *         // config.js properties here
 *     },
 *     interfaceConfig: {
 *         // interface_config.js properties here
 *     },
 *     loggingConfig: {
 *         // logging_config.js properties here
 *     }
 * }.
 * @returns {void}
 */
export function overrideConfigJSON({
    config, interfaceConfig, loggingConfig,
    json,
    dispatch }) {
    for (const configName of Object.keys(json)) {
        let configObj;

        if (configName === 'config') {
            configObj = config;
        } else if (configName === 'interfaceConfig') {
            configObj = interfaceConfig;
        } else if (configName === 'loggingConfig') {
            configObj = loggingConfig;
        }
        if (configObj) {
            const configJSON
                = getWhitelistedJSON(configName, json[configName]);

            if (!_.isEmpty(configJSON)) {


                _.mergeWith(configObj, configJSON, _mergeWithArrayOverwrite);
            }
        }
    }

    if (typeof config === 'object') {
        const { overwriteConfig } = require('./actions');

        dispatch(overwriteConfig(config));
    }
    if (typeof interfaceConfig === 'object') {
        const { overwriteInterfaceConfig } = require('./actions');

        dispatch(overwriteInterfaceConfig(interfaceConfig));

    }
}


/**
 * Apply whitelist filtering for configs with whitelists, skips this for others
 * configs (loggingConfig).
 * Only extracts overridden values for keys we allow to be overridden.
 *
 * @param {string} configName - The config name, one of config,
 * interfaceConfig, loggingConfig.
 * @param {Object} configJSON - The object with keys and values to override.
 * @returns {Object} - The result object only with the keys
 * that are whitelisted.
 */
export function getWhitelistedJSON(configName: string, configJSON: Object): Object {
    if (configName === 'interfaceConfig') {
        return _.pick(configJSON, INTERFACE_CONFIG_WHITELIST);
    } else if (configName === 'config') {
        return _.pick(configJSON, CONFIG_WHITELIST);
    }

    return configJSON;
}

/**
 * Restores a Meet Hour config.js from {@code localStorage} if it was
 * previously downloaded from a specific {@code baseURL} and stored with
 * {@link storeConfig}.
 *
 *
 * @param {string} baseURL - The base URL from which the config.js was
 * previously downloaded and stored with {@code storeConfig}.
 * @returns {?Object} The Meet Hour config.js which was previously downloaded
 * from {@code baseURL} and stored with {@code storeConfig} if it was restored;
 * otherwise, {@code undefined}.
 */
export function restoreConfig(baseURL: string): ?Object {
    const key = `${_CONFIG_STORE_PREFIX}/${baseURL}`;
    const config = jitsiLocalStorage.getItem(key);

    if (config) {
        try {
            return JSON.parse(config) || undefined;
        } catch {
            // Somehow incorrect data ended up in the storage. Clean it up.
            jitsiLocalStorage.removeItem(key);
        }
    }

    return undefined;
}

/**
 * Selector used to get the SSRC-rewriting feature flag.
 *
 * @param {Object} state - The global state.
 * @returns {boolean}
 */
export function getSsrcRewritingFeatureFlag(state) {
    return getFeatureFlag(state, FEATURE_FLAGS.SSRC_REWRITING) ?? true;
}


/**
 * Inspects the hash part of the location URI and overrides values specified
 * there in the corresponding config objects given as the arguments. The syntax
 * is: {@code https://server.com/room#config.debug=true
 * &interfaceConfig.showButton=false&loggingConfig.something=1}.
 *
 * In the hash part each parameter will be parsed to JSON and then the root
 * object will be matched with the corresponding config object given as the
 * argument to this function.
 *
 * @param {Object} config - This is the general config.
 * @param {Object} interfaceConfig - This is the interface config.
 * @param {Object} loggingConfig - The logging config.
 * @param {URI} location - The new location to which the app is navigating to.
 *  @param {dispatch} dispatch - The new location to which the app is navigating to.
 * @returns {void}
 */
export function setConfigFromURLParams({
    config,
    interfaceConfig,
    loggingConfig,
    location,
    dispatch }) {
    const params = parseURLParams(location);
    const json = {};

    config && (json.config = {});
    interfaceConfig && (json.interfaceConfig = {});
    loggingConfig && (json.loggingConfig = {});

    for (const param of Object.keys(params)) {
        let base = json;
        const names = param.split('.');
        const last = names.pop();

        for (const name of names) {
            base = base[name] = base[name] || {};
        }

        base[last] = params[param];
    }

    overrideConfigJSON({ config,
        interfaceConfig,
        loggingConfig,
        json,
        dispatch });
}


/**
 * Returns the pictureInpPicture config.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
export function getPictureInPictureModeConfig(state: Object) {
    return state['features/base/config']?.isPictureInPictureMode || false;
}

/**
 * Get the current working document based on Picture-In-Picture is enabled or not.
 * JqueryID for
 * @param state - State of the application
 * @param JqueryID - For passing the JqueryID.
 */
export function getActiveDOMDocument(state: Object, JqueryID: string = undefined) {
    const isPictureInPictureEnabled = getPictureInPictureModeConfig(state);

    let activeDOM;

    const isPiPContext
        = 'documentPictureInPicture' in window
        && isPictureInPictureEnabled
        && window.self === window.top
        && documentPictureInPicture?.window;

    if (isPiPContext) {
        const pipWindow = documentPictureInPicture.window;

        if (typeof JqueryID === 'undefined') {
            activeDOM = pipWindow?.document ?? document;
        } else {
            const $pipDoc = pipWindow?.document
                ? $(pipWindow.document)
                : null;

            activeDOM = $pipDoc
                ? $pipDoc.find(JqueryID)
                : $(JqueryID);
        }
    } else {
        activeDOM = typeof JqueryID === 'undefined'
            ? document
            : $(JqueryID);
    }

    return activeDOM;
}


/**
 * Selector for determining if the display name is read only.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isNameReadOnly(state: Object): boolean {
    return Boolean(state['features/base/config'].disableProfile
        || state['features/base/config'].readOnlyName);
}

/**
 * Selector for determining if the display name is visible.
 *
 * @param {Object} state - The state of the app.
 * @returns {boolean}
 */
export function isDisplayNameVisible(state: Object): boolean {
    return !state['features/base/config'].hideDisplayName;
}
